import {
    Button, Checkbox, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput,
    Select, CircularProgress, TextField, InputAdornment, Menu, Divider, IconButton, Box,
    useMediaQuery
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { RangeParams } from '@viniarruda/react-month-range-picker/types/MonthRangePicker/types';
import React, { useRef, useState } from 'react';
import MonthRangePickerWrapper from './../MonthRangePicker/MonthRangePicker';
import { ContainerSelectFilters, ContainerFilterPeriod, ContainerFilters } from '../../styles';
import { representationAgeRangeOptions, representationSexOptions } from '../../../../services/utils/representationOptions';
import { CustomOutlinedInputGroup, CustomOutlinedInputRepresentation, StyledDivMonthPicker, StyledTextField } from './InsightsFiltersStyled';
import { useToast } from '../../../../context/Toast';
import { InsightGroupOption } from '../../../../interfaces/insights.interface';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../../i18n/useAppLocale';
import { getLoadingFilterOptions, selectionIndicatorSlotSx } from '../../../filters/filterSelection';

interface InsightsFiltersProps {
    loading: boolean;
    loadingIndicators: boolean;
    loadingEpHistoric: boolean;
    loadingEngHistoric: boolean;
    loadingRelHistoric: boolean;
    loadingSigHistoric: boolean;
    loadingReaHistoric: boolean;
    loadingVitHistoric: boolean;
    dateValue: string;
    setDateValue: (value: string) => void;
    range: RangeParams;
    setRange: React.Dispatch<React.SetStateAction<RangeParams>>;
    selectGroupOptions: InsightGroupOption[];
    representationSelectedOptions: string[];
    setRepresentationSelectedOptions: (options: string[]) => void;
    selectedGroupOptions: string[];
    setSelectedGroups: (options: string[]) => void;
    loadFilteredData: (year?: string, month?: string, monthsRange?: number, overrides?: {
        representationSelectedOptions?: string[];
        selectedGroupOptions?: string[];
        availableGroupOptions?: InsightGroupOption[];
    }) => void;
    loadData: (year?: string, month?: string, monthsRange?: number, availableGroupOptions?: InsightGroupOption[]) => void;

    // NOVOS props:
    // surveysAllMonth: flag vindo da org (true/false) que faz a regra do dia < 20
    surveysAllMonth?: boolean;
    // clientDate: opcional (útil pra testes); se não fornecido usa new Date()
    clientDate?: Date;
}

const InsightsFilters = ({
    loading, loadingIndicators, loadingEpHistoric, loadingEngHistoric, loadingRelHistoric, loadingSigHistoric,
    loadingReaHistoric, loadingVitHistoric,
    dateValue, setDateValue, range, setRange, selectGroupOptions,
    representationSelectedOptions, setRepresentationSelectedOptions,
    selectedGroupOptions, setSelectedGroups,
    loadFilteredData,
    loadData,
    surveysAllMonth = true,
    clientDate
}: InsightsFiltersProps) => {

    const [openMonthRangePicker, setOpenMonthRangePicker] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [rangeText, setRangeText] = useState("");
    const latestRepresentationOptionsRef = useRef<string[]>(representationSelectedOptions);
    const latestGroupOptionsRef = useRef<string[]>(selectedGroupOptions);

    React.useEffect(() => {
        latestRepresentationOptionsRef.current = representationSelectedOptions;
    }, [representationSelectedOptions]);

    React.useEffect(() => {
        latestGroupOptionsRef.current = selectedGroupOptions;
    }, [selectedGroupOptions]);

    const {showToast} = useToast();
    const { t } = useTranslation('insights');
    const appLocale = useAppLocale();
    const sexLabels = [t('sexOptions.male'), t('sexOptions.female'), t('sexOptions.preferNot')];
    const ageLabels = [t('ageOptions.under24'), t('ageOptions.from25to30'), t('ageOptions.from31to35'), t('ageOptions.from36to40'), t('ageOptions.from41to45'), t('ageOptions.from46to50'), t('ageOptions.from51to55'), t('ageOptions.from56to60'), t('ageOptions.over61')];
    const isFilterLoading = loading || loadingIndicators;
    const loadingRepresentationOptions = getLoadingFilterOptions(representationSelectedOptions, isFilterLoading);
    const loadingGroupOptions = getLoadingFilterOptions(selectedGroupOptions, isFilterLoading);

    // breakpoint customizado: a partir de 815px será vertical
    const isMin815 = useMediaQuery('(min-width:815px)');
    const dividerOrientation = isMin815 ? 'vertical' : 'horizontal';
    const dividerIsVertical = dividerOrientation === 'vertical';

    const triggerFilteredData = (representationOptions: string[], groupOptions: string[]) => {
        if (loading || loadingIndicators) return;
        const [year, month] = dateValue.split('-');
        void loadFilteredData(year, month, undefined, {
            representationSelectedOptions: representationOptions,
            selectedGroupOptions: groupOptions,
        });
    };

    const handleChangeRepresentation = (event: any) => {
        const { target: { value } } = event;
        const nextRepresentationOptions = typeof value === 'string' ? value.split(',') : value;
        latestRepresentationOptionsRef.current = nextRepresentationOptions;
        setRepresentationSelectedOptions(nextRepresentationOptions);
        triggerFilteredData(nextRepresentationOptions, latestGroupOptionsRef.current);
    };

    const handleCloseRepresentationSelect = () => {
        setTimeout(() => {
            if (document?.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
                (document.activeElement as HTMLElement).blur();
            }
        }, 0);

    };

    const handleChangeGroup = (event: any) => {
        const { target: { value } } = event;
        const nextGroupOptions = typeof value === 'string' ? value.split(',') : value;
        latestGroupOptionsRef.current = nextGroupOptions;
        setSelectedGroups(nextGroupOptions);
        triggerFilteredData(latestRepresentationOptionsRef.current, nextGroupOptions);
    };

    const handleCloseGroupSelect = () => {
        setTimeout(() => {
            if (document?.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
                (document.activeElement as HTMLElement).blur();
            }
        }, 0);

    };

    const handleRangeChange = (params: RangeParams, clearText: boolean = false) => {
        const monthsRange = (params.endYear - params.startYear) * 12 + (params.endMonth - params.startMonth) + 1;

        if (monthsRange > 12) {
            showToast(t('maxRange'), 'info', 4000);
            setRangeText("");
            setOpenMonthRangePicker(false);
            return;
        }

        setRange(params);

        if (clearText) {
            setRangeText("");
        } else {
            const format = (month: number, year: number) => new Intl.DateTimeFormat(appLocale, { month: '2-digit', year: 'numeric' }).format(new Date(year, month - 1));
            const start = format(params.startMonth, params.startYear);
            const end = format(params.endMonth, params.endYear);
            setRangeText(`${start} - ${end}`);
        }

        setOpenMonthRangePicker(false);
        const year = params.endYear.toString();
        const month = String(params.endMonth).padStart(2, '0');
        setDateValue(`${year}-${month}`);

        const thereIsSomeFilter = (latestRepresentationOptionsRef.current?.length > 0) || (latestGroupOptionsRef.current?.length > 0);
        if (thereIsSomeFilter) {
            loadFilteredData(year, month, monthsRange, {
                representationSelectedOptions: latestRepresentationOptionsRef.current,
                selectedGroupOptions: latestGroupOptionsRef.current,
            });
        } else {
            loadData(year, month, monthsRange, selectGroupOptions);
        }
    };

    // Helper que encapsula a regra: se dia < 20 e surveysAllMonth === false => usar mês anterior
    const getEffectiveEndDate = (baseDate?: Date) => {
        const now = baseDate ?? (clientDate ?? new Date());
        let year = now.getFullYear();
        let month = now.getMonth() + 1; // 1..12

        if (now.getDate() < 20 && !surveysAllMonth) {
            // usar mês anterior
            if (month === 1) {
                month = 12;
                year -= 1;
            } else {
                month -= 1;
            }
        }

        return { year, month };
    };

    const handlePresetRange = (monthsRange: number) => {
        // usa a regra centralizada para descobrir o mês "final" (endMonth/endYear)
        const { year: endYear, month: endMonth } = getEffectiveEndDate();

        // calculamos a data inicial subtraindo monthsRange - 1 meses do endDate
        const endDate = new Date(endYear, endMonth - 1);
        const startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - monthsRange + 1);

        const params: RangeParams = {
            startMonth: startDate.getMonth() + 1,
            startYear: startDate.getFullYear(),
            endMonth: endDate.getMonth() + 1,
            endYear: endDate.getFullYear()
        };

        handleRangeChange(params);
    };

    const handleThisYear = () => {
        const now = clientDate ?? new Date();
        const currentYear = now.getFullYear();
    
        const params: RangeParams = {
            startMonth: 1,
            startYear: currentYear,
            endMonth: 12,
            endYear: currentYear
        };
    
        handleRangeChange(params);
    };
    
    const handlePreviousYear = () => {
        const now = clientDate ?? new Date();
        const currentYear = now.getFullYear();
        const previousYear = currentYear - 1;
    
        const params: RangeParams = {
            startMonth: 1,
            startYear: previousYear,
            endMonth: 12,
            endYear: previousYear
        };
    
        handleRangeChange(params);
    };

    const handleClearFilter = () => {
        // regra idêntica: calcular end a partir da data efetiva
        const { year: endYear, month: endMonth } = getEffectiveEndDate();

        const endDate = new Date(endYear, endMonth - 1);
        const startDate = new Date(endDate);
        // comportamento original: limpar filtro volta 12 meses
        startDate.setMonth(endDate.getMonth() - 12 + 1);

        const params: RangeParams = {
            startMonth: startDate.getMonth() + 1,
            startYear: startDate.getFullYear(),
            endMonth: endDate.getMonth() + 1,
            endYear: endDate.getFullYear()
        };

        handleRangeChange(params, true);
    }

    return (
        <ContainerSelectFilters>
             <ContainerFilterPeriod style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                <FormControl sx={{ width: '100%', background: '#FFF', minHeight: '52.16px', borderRadius: '6px', cursor: 'pointer' }}>
                    <div ref={anchorRef} style={{ cursor: 'pointer' }}>
                        <StyledTextField
                            label={t('filterPeriod')}
                            id="period-filter"
                            value={rangeText}
                            disabled={loading}
                            onClick={() => setOpenMonthRangePicker(true)}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton disabled={loading} onClick={() => setOpenMonthRangePicker(true)}>
                                            <DateRangeIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            InputLabelProps={{ shrink: Boolean(rangeText) }}
                            sx={{ width: '100%', background: '#FFF', borderRadius: '6px' }}
                        />
                    </div>
                    <Menu open={openMonthRangePicker} anchorEl={anchorRef.current} disableScrollLock={true} onClose={() => setOpenMonthRangePicker(false)}>
                        <div>
                            <StyledDivMonthPicker style={{ display: 'flex' }}>
                                <MonthRangePickerWrapper
                                    dateRange={range}
                                    setDateRange={setRange}
                                    onChange={handleRangeChange}
                                    columns={1}
                                    locale={appLocale}
                                />
                                <Divider
                                    orientation={dividerOrientation as 'horizontal' | 'vertical'}
                                    flexItem={dividerIsVertical}
                                    sx={{
                                        borderColor: 'black',
                                        margin: dividerIsVertical ? '1.2em 0' : '0 1.2em',
                                        alignSelf: dividerIsVertical ? 'stretch' : 'auto',
                                    }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.5em', padding: '1em' }}>
                                    <Button sx={{ borderRadius: '1em' }} variant='outlined' onClick={() => handlePresetRange(3)}>{t('last3')}</Button>
                                    <Button sx={{ borderRadius: '1em' }} variant='outlined' onClick={() => handlePresetRange(6)}>{t('last6')}</Button>
                                    <Button sx={{ borderRadius: '1em' }} variant='outlined' onClick={() => handlePresetRange(12)}>{t('last12')}</Button>
                                    <Button sx={{ borderRadius: '1em' }} variant='outlined' onClick={handleThisYear}>{t('thisYear')}</Button>
                                    <Button sx={{ borderRadius: '1em' }} variant='outlined' onClick={handlePreviousYear}>{t('previousYear')}</Button>
                                    <Button sx={{ borderRadius: '1em' }} onClick={handleClearFilter} variant='outlined'>{t('clear')}</Button>
                                </div>
                            </StyledDivMonthPicker>
                        </div>
                    </Menu>
                </FormControl>
            </ContainerFilterPeriod>

            {/* O resto do JSX permanece idêntico */}
            <ContainerFilters>
                <FormControl sx={{ width: '100%', background: '#FFF', minHeight: '52.16px', borderRadius: '6px' }}>
                    <InputLabel id="multiple-select">{t('filterRepresentation')}</InputLabel>
                    <Select
                        labelId="multiple-select"
                        id="multiple-select"
                        multiple
                        value={representationSelectedOptions}
                        onChange={handleChangeRepresentation}
                        onClose={handleCloseRepresentationSelect}
                        IconComponent={FilterListIcon}
                        input={<CustomOutlinedInputRepresentation hasSelection={representationSelectedOptions.length > 0} label={t('filterRepresentation')} />}
                        renderValue={(selected) => (selected as string[]).map(value => { const sexIndex = representationSexOptions.indexOf(value); const ageIndex = representationAgeRangeOptions.indexOf(value); return sexIndex >= 0 ? sexLabels[sexIndex] : ageIndex >= 0 ? ageLabels[ageIndex] : value; }).join(', ')}
                        MenuProps={{
                            disableAutoFocusItem: true,
                            disableAutoFocus: true,
                            disableScrollLock: true,
                            MenuListProps: {
                                disableListWrap: true,
                            },
                        }}
                    >
                        <ListSubheader>{t('sex')}</ListSubheader>
                        {representationSexOptions.map((name: string, index: number) => (
                            <MenuItem key={name} value={name} disabled={loading || loadingIndicators} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                                <Box sx={selectionIndicatorSlotSx}>
                                    {loadingRepresentationOptions.has(name) ?
                                        <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                                        <Checkbox checked={representationSelectedOptions.indexOf(name) > -1} />}
                                </Box>
                                <ListItemText primary={sexLabels[index]} />
                                <Button
                                    sx={{
                                        display: representationSelectedOptions.indexOf(name) > -1 ? 'block' : 'none',
                                    }}
                                    variant="outlined"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleChangeRepresentation({target: { value: [name] }});
                                    }}
                                >{t('only')}</Button>
                            </MenuItem>
                        ))}

                        <ListSubheader>{t('age')}</ListSubheader>
                        {representationAgeRangeOptions.map((name: string, index: number) => (
                            <MenuItem key={name} value={name} disabled={loading || loadingIndicators} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                                <Box sx={selectionIndicatorSlotSx}>
                                    {loadingRepresentationOptions.has(name) ?
                                        <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                                        <Checkbox checked={representationSelectedOptions.indexOf(name) > -1} />}
                                </Box>
                                <ListItemText primary={ageLabels[index]} />
                                <Button
                                    sx={{
                                    display: representationSelectedOptions.indexOf(name) > -1 ? 'block' : 'none',
                                    }}
                                    variant="outlined"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleChangeRepresentation({target: { value: [name] }});
                                    }}
                                >{t('only')}</Button>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ width: '100%', background: '#FFF', minHeight: '52.16px', borderRadius: '6px' }}>
                    <InputLabel id="multiple-select-group">{t('filterGroup')}</InputLabel>
                    <Select
                        labelId="multiple-select-group"
                        id="multiple-select-group"
                        multiple
                        value={selectedGroupOptions}
                        onChange={handleChangeGroup}
                        onClose={handleCloseGroupSelect}
                        IconComponent={FilterListIcon}
                        input={<CustomOutlinedInputGroup hasSelection={selectedGroupOptions.length > 0} label={t('filterGroup')} />}
                        renderValue={(selected) => {
                            const selectedValues = selected as string[];
                            return selectedValues.map(val => {
                                const foundOption = selectGroupOptions.find((option) => option.value === val);
                                return foundOption ? foundOption.label : '';
                            }).join(', ');
                        }}
                        MenuProps={{
                            disableAutoFocusItem: true,
                            disableAutoFocus: true,
                            disableScrollLock: true,
                            MenuListProps: {
                                disableListWrap: true,
                            },
                        }}
                    >
                        {selectGroupOptions.map((group) => (
                            <MenuItem key={group.label} value={group.value} disabled={loading || loadingIndicators} sx={{ '&.Mui-disabled': { cursor: 'progress', opacity: 0.65 } }}>
                                <Box sx={selectionIndicatorSlotSx}>
                                    {loadingGroupOptions.has(group.value) ?
                                        <CircularProgress size={18} sx={{ color: 'gray' }} /> :
                                        <Checkbox checked={selectedGroupOptions.indexOf(group.value) > -1} />}
                                </Box>
                                <ListItemText primary={group.label} />
                                <Button
                                    sx={{
                                    display: selectedGroupOptions.indexOf(group.value) > -1 ? 'block' : 'none',
                                    }}
                                    variant="outlined"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleChangeGroup({target: { value: [group.value] }});
                                    }}
                                >{t('only')}</Button>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </ContainerFilters>
        </ContainerSelectFilters>
    );
};


export default InsightsFilters;
